import { RenderStateCamera } from "@novorender/api";
import { useCallback, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import {
    highlightCollectionsActions,
    useDispatchHighlightCollections,
    useHighlightCollections,
} from "contexts/highlightCollections";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import {
    GroupStatus,
    InternalTemporaryGroup,
    objectGroupsActions,
    useDispatchObjectGroups,
    useLazyObjectGroups,
} from "contexts/objectGroups";
import { selectionBasketActions, useDispatchSelectionBasket, useSelectionBasket } from "contexts/selectionBasket";
import { coordinatesActions, selectCoordinates } from "features/coordinates";
import { measureActions, selectMeasureEntities } from "features/measure/measureSlice";
import { clippingOutlineLaserActions, selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLines } from "features/pointLine/pointLineSlice";
import { useSetupVersionComparisonPropertyTree } from "features/propertyTree/hooks/useSetupVersionComparisonPropertyTree";
import {
    renderActions,
    selectClippingPlanes,
    selectDefaultVisibility,
    selectInitialCamera,
    selectPicker,
} from "features/render";
import { cloneCameraState } from "features/render/hooks/useHandleCameraMoved";
import { CameraType } from "features/render/types";
import { loadScene } from "features/render/utils";
import { cloneClippingPlanes } from "features/sectionPlane/sectionPlaneUndoRedo";
import { undoRedoActions } from "features/undoRedo";
import { useSceneId } from "hooks/useSceneId";
import { explorerActions } from "slices/explorer";
import { selectBackendProvider, selectIsVersionComparison, selectPipelineConfig } from "slices/explorer";
import { telemetry } from "telemetry";

export type ResetViewFn = (options?: {
    resetCamera?: boolean;
    resetVersionComparisonPropertyTree?: boolean;
    preservePicker?: boolean;
}) => Promise<void>;

export function useResetView(): ResetViewFn {
    const {
        state: { view, db, internalEventEmitter },
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const defaultCamera = useAppSelector(selectInitialCamera);
    const backendProvider = useAppSelector(selectBackendProvider);
    const dispatch = useAppDispatch();
    const dispatchHighlightCollections = useDispatchHighlightCollections();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const dispatchSelectionBasket = useDispatchSelectionBasket();
    const objectGroups = useLazyObjectGroups();
    const dispatchObjectGroups = useDispatchObjectGroups();
    const setupVersionComparisonPropertyTree = useSetupVersionComparisonPropertyTree();
    const isVersionComparison = useAppSelector(selectIsVersionComparison);
    const highlightedObjects = useHighlighted();
    const hiddenObjects = useHidden();
    const selectionBasket = useSelectionBasket();
    const highlightCollections = useHighlightCollections();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    // Only fetch pipelineConfig for Novorender backend (used in version comparison)
    const pipelineConfig = useAppSelector((state) =>
        backendProvider !== "procore" ? selectPipelineConfig(state) : undefined,
    );
    // Measurements
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const dimensions = useAppSelector(selectMeasureEntities);
    // Section related
    const sectionPlanes = useAppSelector(selectClippingPlanes);
    const currentPicker = useAppSelector(selectPicker);

    const clearObjectHighlights = () => {
        dispatchHidden(hiddenActions.setIds([]));
        dispatchHighlighted(highlightActions.setIds([]));
        dispatchSelectionBasket(selectionBasketActions.set([]));
        dispatchHighlightCollections(highlightCollectionsActions.clearAll());
    };

    const lastSavedCameraState = useRef<RenderStateCamera | null>(null);

    const undoResetView = useCallback(() => {
        const dimensionsEntitiesToRestore = dimensions.map((set) => structuredClone(set));

        dispatchHidden(hiddenActions.setIds(hiddenObjects.idArr));
        dispatchHighlighted(highlightActions.setIds(highlightedObjects.idArr));
        dispatchSelectionBasket(selectionBasketActions.set(selectionBasket.idArr));
        dispatchHighlightCollections(highlightCollectionsActions.setAll(highlightCollections));
        dispatch(renderActions.setDefaultVisibility(defaultVisibility));
        dispatch(coordinatesActions.setCoordinates(coordinates));
        dispatch(clippingOutlineLaserActions.setLasers(lasers));
        dispatch(pointLineActions.setPointLines(pointLines));
        if (dimensionsEntitiesToRestore.length === 1) {
            dispatch(measureActions.setSelectedEntities(dimensionsEntitiesToRestore[0]));
        } else {
            dimensionsEntitiesToRestore.forEach((entities, i) =>
                dispatch(measureActions.setMeasureSet({ idx: i, entities })),
            );
        }
        const clonedSectionPlanes = cloneClippingPlanes(sectionPlanes.planes || []);
        dispatch(
            renderActions.setClippingPlanes(
                clonedSectionPlanes.length === 0
                    ? { planes: clonedSectionPlanes, enabled: false }
                    : { planes: clonedSectionPlanes, enabled: true },
            ),
        );

        if (lastSavedCameraState.current) {
            dispatch(
                renderActions.setCamera({
                    type:
                        lastSavedCameraState.current.kind === "pinhole" ? CameraType.Pinhole : CameraType.Orthographic,
                    goTo: cloneCameraState(lastSavedCameraState.current),
                }),
            );
        }
    }, [
        dispatch,
        dispatchHidden,
        hiddenObjects.idArr,
        dispatchHighlighted,
        highlightedObjects.idArr,
        dispatchSelectionBasket,
        selectionBasket.idArr,
        dispatchHighlightCollections,
        highlightCollections,
        defaultVisibility,
        coordinates,
        lasers,
        pointLines,
        dimensions,
        sectionPlanes,
    ]);

    return async ({
        resetCamera = true,
        resetVersionComparisonPropertyTree = true,
        preservePicker = false,
    }:
        | {
              resetCamera?: boolean;
              resetVersionComparisonPropertyTree?: boolean;
              preservePicker?: boolean;
          }
        | undefined = {}) => {
        async function resetView() {
            try {
                // For Procore backend, don't call loadScene (Novorender API)
                // Scene data is already loaded via Procore APIs
                if (backendProvider === "procore") {
                    const initialCamera = resetCamera ? structuredClone(defaultCamera) : undefined;

                    const sceneData = {
                        title: "",
                        objectGroups: [],
                        settings: undefined,
                        customProperties: {},
                        projectId: "",
                    };

                    dispatch(renderActions.clearAllObjectColor());
                    dispatch(
                        renderActions.resetView({
                            sceneData,
                            initialCamera,
                        }),
                    );
                } else {
                    // Novorender backend - use loadScene API
                    const [{ url: _url, db: _db, ...sceneData }, camera] = await loadScene(sceneId);
                    const groups = sceneData.objectGroups ?? [];
                    const initialCamera = resetCamera ? (camera ?? structuredClone(defaultCamera)) : undefined;

                    dispatch(renderActions.clearAllObjectColor());
                    dispatch(
                        renderActions.resetView({
                            sceneData,
                            initialCamera,
                        }),
                    );

                    dispatchObjectGroups(
                        objectGroupsActions.set(
                            objectGroups.current
                                .filter((group) => group.grouping !== InternalTemporaryGroup.BIMcollab)
                                .map((group) => {
                                    const saved = groups.find((grp) => group.id === grp.id);

                                    if (saved) {
                                        let status: GroupStatus;
                                        if (saved.selected) {
                                            status = GroupStatus.Selected;
                                        } else if (saved.hidden) {
                                            status = GroupStatus.Hidden;
                                        } else if (saved.frozen) {
                                            status = GroupStatus.Frozen;
                                        } else {
                                            status = GroupStatus.None;
                                        }
                                        return { ...group, status, opacity: saved.opacity ?? 0 };
                                    }

                                    return group;
                                })
                                .sort(
                                    (a, b) =>
                                        groups.findIndex((grp) => grp.id === a.id) -
                                        groups.findIndex((grp) => grp.id === b.id),
                                ),
                        ),
                    );
                }

                // Emit viewReset event through internal EventEmitter.
                // NOTE: This intentionally happens immediately after render.resetView has
                // completed, but before clearing clipping outlines, coordinates and object
                // highlights below. Consumers of the event rely on seeing the freshly
                // reset scene/camera state while any existing auxiliary UI state is still
                // present, so the ordering here is part of the event's contract.
                // In lib mode, WebviewerBridge will propagate this to the public webviewer.events API.
                if (internalEventEmitter) {
                    await internalEventEmitter.emit("viewReset", {
                        timestamp: Date.now(),
                        sceneId,
                        cameraReset: resetCamera,
                    });
                }

                dispatch(clippingOutlineLaserActions.clear());
                dispatch(coordinatesActions.setHideCoordinateGizmo(false));
                dispatch(coordinatesActions.clear());
                clearObjectHighlights();

                // Only restore object groups for Novorender backend
                // Procore doesn't have saved groups yet

                if (resetVersionComparisonPropertyTree && db && view?.renderState.scene?.url && isVersionComparison) {
                    void setupVersionComparisonPropertyTree(db, view, pipelineConfig?.version, backendProvider);
                }
            } catch (e) {
                console.error("Failed to reset view:", e);
                telemetry.trackCaughtException({
                    source: "home.useResetView.resetView",
                    category: "data",
                    error: e,
                    severity: "error",
                });
                dispatch(
                    explorerActions.setSnackbarMessage({
                        msg: "Failed to reset view. Please try again.",
                        closeAfter: 4000,
                    }),
                );
            }
        }
        dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
        if (view?.renderState.camera) {
            lastSavedCameraState.current = cloneCameraState(view.renderState.camera);
        }
        await resetView();

        // Restore the active picker so the user stays in their current mode
        // (e.g. measurement or section) even after the model state is cleared.
        if (preservePicker) {
            dispatch(renderActions.setPicker(currentPicker));
        }

        dispatch(
            undoRedoActions.push({
                description: "Reset View",
                undoCallback: async () => {
                    dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
                    undoResetView();
                },
                redoCallback: async () => {
                    dispatch(renderActions.setRecordCameraMoveUndoRedo(false));
                    await resetView();
                    if (preservePicker) {
                        dispatch(renderActions.setPicker(currentPicker));
                    }
                },
            }),
        );
    };
}
